import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";

import {
  Disclose,
  EmptyState,
  Loading,
  Permission,
  Refusal,
  hostConfig,
  kernel,
} from "@lopie/kernel-ui";
import ScoringPanel from "./ScoringPanel.jsx";

const PAGE = 200;
const MAX_PAGES = 25;

const AUDIT_THRESHOLD = 60;

function fmtMoney(minorUnits, currency) {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(
      minorUnits / 100,
    );
  } catch {
    return `${(minorUnits / 100).toFixed(2)} ${currency}`;
  }
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function parseRisk(content) {
  const text = (content || "").trim();
  try {
    const obj = JSON.parse(text);
    if (obj && typeof obj.score === "number" && typeof obj.reason === "string") {
      return { score: Math.max(0, Math.min(100, obj.score)), reason: obj.reason.trim() };
    }
  } catch {
    // Not JSON — fall through to the legacy prose format below.
  }
  const m = /^Risk:\s*(\d{1,3})\s*\/\s*100\s*[—-]\s*([\s\S]*)$/.exec(text);
  if (!m) return null;
  const score = Math.max(0, Math.min(100, Number(m[1])));
  const reason = m[2].trim().replace(/^Payment:[A-Za-z0-9]+\s+(is|was)\s+/, "This payment $1 ");
  return { score, reason };
}

function band(score) {
  if (score == null) return "unscored";
  if (score >= 60) return "high";
  if (score >= 25) return "medium";
  return "low";
}

function RiskChip({ score }) {
  const theme = useTheme();
  const palette = theme.vars?.palette || theme.palette;
  const b = band(score);
  const by = {
    high: { bg: palette.error.main, fg: palette.error.contrastText, label: "High" },
    medium: { bg: palette.warning.main, fg: palette.warning.contrastText, label: "Medium" },
    low: { bg: palette.success.main, fg: palette.success.contrastText, label: "Low" },
    unscored: { bg: palette.action.disabledBackground, fg: palette.text.secondary, label: "Not yet scored" },
  }[b];
  return (
    <Chip
      size="small"
      label={score == null ? by.label : `${score} · ${by.label}`}
      sx={{ bgcolor: by.bg, color: by.fg, fontWeight: 600 }}
    />
  );
}

function TriageCell({ score, status, busy, onAudit, onDismiss }) {
  if (score == null || score < AUDIT_THRESHOLD) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  if (status === "audit") {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <Chip size="small" color="warning" label="Flagged for audit" />
        <Button size="small" disabled={busy} onClick={onDismiss}>
          Dismiss instead
        </Button>
      </Stack>
    );
  }
  if (status === "dismissed") {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <Chip size="small" label="Dismissed" />
        <Button size="small" disabled={busy} onClick={onAudit}>
          Audit instead
        </Button>
      </Stack>
    );
  }
  return (
    <Stack direction="row" spacing={1}>
      <Button size="small" variant="outlined" color="warning" disabled={busy} onClick={onAudit}>
        Audit
      </Button>
      <Button size="small" disabled={busy} onClick={onDismiss}>
        Dismiss
      </Button>
    </Stack>
  );
}

async function fetchAll(get, path) {
  let cursor;
  const items = [];
  for (let i = 0; i < MAX_PAGES; i += 1) {
    const body = await get(path, cursor ? { limit: PAGE, cursor } : { limit: PAGE });
    items.push(...(body.items || []));
    if (!body.has_more) return { items, body };
    cursor = body.cursor;
  }
  return { items, body: null };
}

export default function App({ token, claims, onSignOut }) {
  const [payments, setPayments] = useState(null);
  const [findings, setFindings] = useState(new Map());
  const [tags, setTags] = useState([]);
  const [declaredTags, setDeclaredTags] = useState(null);
  const [failure, setFailure] = useState(null);
  const [refusals, setRefusals] = useState({});
  const [sort, setSort] = useState({ key: "score", dir: "desc" });
  const [filter, setFilter] = useState("all");
  const [running, setRunning] = useState(false);
  const [runNote, setRunNote] = useState(null);
  const [triage, setTriageMap] = useState(new Map());
  const [triageBusy, setTriageBusy] = useState(new Set());

  const get = useMemo(() => kernel(token), [token]);
  const conf = hostConfig();
  const ownApi = conf?.appTxnRiskApi || "";

  const own = useCallback(
    async (path, init) => {
      const r = await fetch(ownApi + path, {
        ...init,
        headers: {
          Authorization: `Bearer ${token}`,
          "content-type": "application/json",
          ...(init?.headers || {}),
        },
      });
      const text = await r.text();
      let body = null;
      try {
        body = JSON.parse(text);
      } catch {
        body = null;
      }
      if (!r.ok) throw Object.assign(new Error("refused"), { status: r.status, body });
      return body;
    },
    [ownApi, token],
  );

  const load = useCallback(async () => {
    try {
      const [apps, schema] = await Promise.all([get("/apps"), get("/schema")]);
      const self = (apps.apps || []).find((a) => a.is_self);
      const declared = (self?.reads || []).map((g) => g.type);
      const described = schema.types || [];
      const where = Object.fromEntries(
        described.filter((t) => t.collection).map((t) => [t.type, t.collection]),
      );
      const nextRefusals = {};
      let paymentItems = [];
      let memoryItems = [];
      let permitted = [];

      if (declared.includes("Payment") && where.Payment) {
        try {
          const { items, body } = await fetchAll(get, `/${where.Payment}`);
          paymentItems = items;
          if (body) permitted = body.permitted_tags || [];
        } catch (e) {
          nextRefusals.Payment = e;
        }
      }
      if (declared.includes("Memory") && where.Memory) {
        try {
          const { items } = await fetchAll(get, `/${where.Memory}`);
          memoryItems = items;
        } catch (e) {
          nextRefusals.Memory = e;
        }
      }

      setRefusals(nextRefusals);
      setTags(permitted);

      setPayments(paymentItems.filter((p) => p.fields?.amount !== undefined));

      const bySubject = new Map();
      for (const m of memoryItems) {
        if (m.fields?.active === "false") continue;
        const cites = m.fields?.derived_from;
        const list = Array.isArray(cites) ? cites : (cites ? [cites] : []);
        const paymentCite = list.find(
          (c) => typeof c === "string" && c.startsWith("Payment:"),
        );
        if (!paymentCite) continue;
        const id = paymentCite.slice("Payment:".length);
        const at = m.provenance?.at || "";
        const prior = bySubject.get(id);
        if (!prior || at > prior.at) bySubject.set(id, { at, memory: m });
      }
      const parsed = new Map();
      for (const [id, { memory }] of bySubject) {
        const p = parseRisk(memory.fields?.content);
        parsed.set(id, {
          score: p?.score ?? null,
          reason: p?.reason || memory.fields?.content || "",
          at: memory.provenance?.at,
        });
      }
      setFindings(parsed);
      setFailure(null);
    } catch (e) {
      setFailure(e);
    }

    try {
      const records = await own("/records");
      const map = new Map();
      for (const row of records.items || []) {
        if (typeof row.id === "string" && row.id.startsWith("triage#") && row.paymentId) {
          map.set(row.paymentId, { status: row.status, at: row.decided_at, by: row.decided_by });
        }
      }
      setTriageMap(map);
    } catch {
      setTriageMap(new Map());
    }
  }, [get, own]);

  useEffect(() => {
    load();
  }, [load]);

  const runNow = useCallback(async () => {
    setRunning(true);
    setRunNote(null);
    try {
      const started = await own("/score-now", { method: "POST" });
      const runId = started.run_id;
      const instance = started.instance || "";
      let last = started;
      if (runId) {
        for (let i = 0; i < 60; i += 1) {
          await new Promise((r) => setTimeout(r, 3000));
          const qs = instance ? `?instance=${encodeURIComponent(instance)}` : "";
          // eslint-disable-next-line no-await-in-loop
          last = await own(`/score-status/${runId}${qs}`);
          if (last.status && last.status !== "running") break;
        }
      }
      setRunNote({ ok: true, status: last.status || "started" });
      await load();
    } catch (e) {
      setRunNote({ ok: false, error: e });
    } finally {
      setRunning(false);
    }
  }, [own, load]);

  const setTriage = useCallback(async (paymentId, status) => {
    setTriageBusy((s) => new Set(s).add(paymentId));
    try {
      const saved = await own(`/triage/${encodeURIComponent(paymentId)}`, {
        method: "PUT",
        body: JSON.stringify({ status }),
      });
      setTriageMap((m) => {
        const next = new Map(m);
        next.set(paymentId, { status: saved.status, at: saved.decided_at, by: saved.decided_by });
        return next;
      });
    } finally {
      setTriageBusy((s) => {
        const next = new Set(s);
        next.delete(paymentId);
        return next;
      });
    }
  }, [own]);

  const rows = useMemo(() => {
    if (!payments) return [];
    let out = payments.map((p) => {
      const f = findings.get(p.id);
      const t = triage.get(p.id);
      return {
        id: p.id,
        direction: p.fields.direction,
        amount: p.fields.amount,
        currency: p.fields.currency,
        paidAt: p.fields.paid_at,
        reference: p.fields.reference || "",
        status: p.fields.status || "",
        score: f?.score ?? null,
        reason: f?.reason || "",
        scoredAt: f?.at || null,
        triageStatus: t?.status || null,
      };
    });
    if (filter === "flagged") out = out.filter((r) => (r.score ?? 0) >= 60);
    if (filter === "unscored") out = out.filter((r) => r.score == null);
    out.sort((a, b) => {
      const dir = sort.dir === "asc" ? 1 : -1;
      if (sort.key === "score") {
        const av = a.score ?? -1;
        const bv = b.score ?? -1;
        return (av - bv) * dir;
      }
      if (sort.key === "amount") return (a.amount - b.amount) * dir;
      return (a.paidAt < b.paidAt ? -1 : a.paidAt > b.paidAt ? 1 : 0) * dir;
    });
    return out;
  }, [payments, findings, triage, filter, sort]);

  const summary = useMemo(() => {
    const scored = rows.filter((r) => r.score != null);
    const high = rows.filter((r) => (r.score ?? 0) >= AUDIT_THRESHOLD);
    const highAmount = high.reduce((s, r) => s + (r.direction === "outbound" ? r.amount : 0), 0);
    const pending = high.filter((r) => !r.triageStatus);
    const lastScored = scored.reduce((max, r) => (r.scoredAt > max ? r.scoredAt : max), "");
    return {
      total: rows.length,
      scored: scored.length,
      high: high.length,
      highAmount,
      pending: pending.length,
      lastScored: lastScored || null,
    };
  }, [rows]);

  if (failure) {
    return (
      <Box sx={{ p: 3 }}>
        <Refusal failure={failure} actor="Transaction Risk" onRetry={load} />
      </Box>
    );
  }
  if (!payments) return <Loading label="Reading the kernel" minHeight="60vh" />;

  return (
    <Stack spacing={3} sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap" }}
      >
        <Typography variant="h1">Transaction Risk</Typography>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {claims?.username || ""}
          </Typography>
          <Typography
            component="button"
            variant="body2"
            onClick={onSignOut}
            sx={{ background: "none", border: 0, cursor: "pointer", color: "primary.main" }}
          >
            Sign out
          </Typography>
        </Stack>
      </Stack>

      <Permission
        gate={
          "Governed by this app's reviewed manifest — what is below is that "
          + "ceiling intersected with your own groups. The score on each row "
          + "is a separate opinion this app's own agent wrote, not a kernel "
          + "fact, and only exists for a payment the agent has already run "
          + "against."
        }
        groups={claims?.["cognito:groups"] || []}
        effective={{ permitted: tags, declared: declaredTags }}
      />

      {refusals.Payment ? (
        <Refusal failure={refusals.Payment} actor="Transaction Risk" onRetry={load} />
      ) : null}

      <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 160 }}>
          <Typography variant="overline" color="text.secondary">Transactions</Typography>
          <Typography variant="h4">{summary.total}</Typography>
          <Typography variant="caption" color="text.secondary">
            in view now — not a forward guarantee
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 160 }}>
          <Typography variant="overline" color="text.secondary">Scored</Typography>
          <Typography variant="h4">{summary.scored} / {summary.total}</Typography>
          <Typography variant="caption" color="text.secondary">
            {summary.lastScored ? `last scored ${fmtDate(summary.lastScored)}` : "agent has not run yet"}
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 200, borderColor: summary.high ? "error.main" : undefined }}>
          <Typography variant="overline" color="text.secondary">Flagged high risk</Typography>
          <Typography variant="h4">{summary.high}</Typography>
          <Typography variant="caption" color="text.secondary">
            {summary.highAmount ? `${fmtMoney(summary.highAmount, "USD")} outbound` : "none currently"}
            {summary.high ? ` · ${summary.pending} awaiting review` : ""}
          </Typography>
        </Paper>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          onChange={(_e, v) => v && setFilter(v)}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="flagged">High risk</ToggleButton>
          <ToggleButton value="unscored">Not yet scored</ToggleButton>
        </ToggleButtonGroup>
        <Button variant="contained" disabled={running} onClick={runNow}>
          {running ? "Scoring…" : "Run scoring now"}
        </Button>
        {runNote ? (
          runNote.ok ? (
            <Typography variant="body2" color="text.secondary">
              Run finished ({runNote.status}) — list refreshed.
            </Typography>
          ) : (
            <Alert severity="error" variant="outlined" sx={{ py: 0 }}>
              {runNote.error?.body?.detail || "The run could not be started."}
            </Alert>
          )
        ) : null}
      </Stack>

      {rows.length === 0 ? (
        <EmptyState title="Nothing to show for this filter" />
      ) : (
        <Paper variant="outlined" sx={{ overflow: "auto", minWidth: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sort.key === "paidAt"}
                    direction={sort.key === "paidAt" ? sort.dir : "desc"}
                    onClick={() => setSort((s) => ({
                      key: "paidAt", dir: s.key === "paidAt" && s.dir === "desc" ? "asc" : "desc",
                    }))}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>Direction</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sort.key === "amount"}
                    direction={sort.key === "amount" ? sort.dir : "desc"}
                    onClick={() => setSort((s) => ({
                      key: "amount", dir: s.key === "amount" && s.dir === "desc" ? "asc" : "desc",
                    }))}
                  >
                    Amount
                  </TableSortLabel>
                </TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sort.key === "score"}
                    direction={sort.key === "score" ? sort.dir : "desc"}
                    onClick={() => setSort((s) => ({
                      key: "score", dir: s.key === "score" && s.dir === "desc" ? "asc" : "desc",
                    }))}
                  >
                    Risk
                  </TableSortLabel>
                </TableCell>
                <TableCell>Why</TableCell>
                <TableCell>Review</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(r.paidAt)}</TableCell>
                  <TableCell sx={{ textTransform: "capitalize" }}>{r.direction}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {fmtMoney(r.amount, r.currency)}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>{r.reference}</TableCell>
                  <TableCell><RiskChip score={r.score} /></TableCell>
                  <TableCell sx={{ maxWidth: 360, color: "text.secondary" }}>
                    {r.reason || "—"}
                  </TableCell>
                  <TableCell>
                    <TriageCell
                      score={r.score}
                      status={r.triageStatus}
                      busy={triageBusy.has(r.id)}
                      onAudit={() => setTriage(r.id, "audit")}
                      onDismiss={() => setTriage(r.id, "dismissed")}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <ScoringPanel token={token} />

      <Disclose label="What this does not prove">
        <Stack spacing={1.5} sx={{ maxWidth: "62ch" }}>
          <Typography variant="body2" color="text.secondary">
            A risk score is this app&apos;s agent comparing one payment against
            every other payment it can currently see, for patterns worth a
            person&apos;s attention — it is a heuristic judgement, not a fraud
            determination, and it is defensible by the specific comparison
            named in the &quot;why&quot; column, nothing more.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            A payment with no score has not been judged yet, which is
            different from having been judged safe. Scores only update when
            the agent runs — on the hourly timer, or on demand above.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Audit/Dismiss is this app&apos;s own record of a decision, not a
            kernel fact — it changes nothing about the Payment or its score,
            and dismissing a flag does not make the pattern the agent found
            go away, only marks that somebody looked.
          </Typography>
        </Stack>
      </Disclose>
    </Stack>
  );
}
